import mime from "react-native-mime-types";
import {getData} from "../indexeddb/getData";
import {damsFetch} from "../app/damsFetch";
import {clientLog} from "../clientLog";
import {DB_VERSION} from "../indexeddb/dbVersion";


let instance = null;

export const ObjectDefinitions = () => {
    if (instance) {
        return instance;
    }

    const DB_STORE = 'object_definitions';

    const _loadObjectDefinitions = (() => {
        let cache = null;
        let promise = null;
        return async () => {
            if (cache) return cache;
            if (promise) return promise;
            promise = (async () => {
                try {
                    cache = await getData(DB_VERSION, DB_STORE, 1);
                    return cache;
                } catch (error) {
                    if (!cache) {
                        clientLog('error',
                            `failed to load object definitions from IndexedDB, using DAMS backend: ${error}`,
                            'objectdefinitions');
                        cache = await _loadObjectDefinitionsFallback();
                    }
                    return cache;
                } finally {
                    promise = null;
                }
            })();
            return promise;
        };
    })();

    const _loadObjectDefinitionsFallback = async () => {
        try {
            return await damsFetch('/object-definitions/', {
                method: "GET",
            });
        } catch (error) {
            clientLog('error',
                `failed to load object definitions from DAMS backend: ${error}`,
                'objectdefinitions');
            return [];
        }
    };

    const getLabelColor = (objectType) => {
        switch (objectType) {
            case "audio":
                return "tomato";
            case "image":
                return "deepskyblue";
            case "video":
                return "orchid";
            case "acrobat":
                return "orange";
            case "document":
                return "orange";
            case "spreadsheet":
                return "green";
            case "presentation":
                return "green";
            case "compressed":
                return "gold";
            case "geodata":
                return "gold";
            default:
                return "deepskyblue";
        }
    };

    const getObjectDefinitionIndex = async (key, value, objectDefinitions = null) => {
        if (objectDefinitions === null) {
            objectDefinitions = await _loadObjectDefinitions();
        }
        if (!Array.isArray(objectDefinitions)) {
            return -1
        }
        return objectDefinitions.findIndex(o => o[key] === value);
    };

    const getFileExtensionFromFilename = filename => {
        if (!filename || filename.indexOf('.') === -1) {
            return '';
        }
        return filename.substr(filename.lastIndexOf(".") + 1).toLowerCase();
    };

    const getFileExtensionFromMimeType = async (mimeType) => {
        let ext = mime.extension(mimeType);
        if (!ext) {
            const objectDefinitions = await _loadObjectDefinitions();
            const ix = await getObjectDefinitionIndex('mimeType', mimeType, objectDefinitions);
            ext = ix > -1 ? objectDefinitions[ix].extension : null;
        }
        return ext;
    };

    const getMimeTypeFromFileExtension = async (extension) => {
        let mimeType = mime.lookup(extension);
        if (!mimeType) {
            const objectDefinitions = await _loadObjectDefinitions();
            const ix = await getObjectDefinitionIndex('extension', extension, objectDefinitions);
            mimeType = ix > -1 ? objectDefinitions[ix].mimeType : null;
        }
        return mimeType;
    };

    const getMimeTypeFromFilename = async (filename) => {
        const ext = getFileExtensionFromFilename(filename);
        return await getMimeTypeFromFileExtension(ext);
    };

    const getIconFromFilename = async (filename) => {
        const objectDefinitions = await _loadObjectDefinitions();
        if (!Array.isArray(objectDefinitions)) {
            return null;
        }
        const extension = getFileExtensionFromFilename(filename);
        const ix = await getObjectDefinitionIndex('extension', extension, objectDefinitions);
        return ix > -1 ? objectDefinitions[ix].iconType : null;
    };

    const getIconFromMimeType = async (mimeType) => {
        const objectDefinitions = await _loadObjectDefinitions();
        const ix = await getObjectDefinitionIndex('mimeType', mimeType, objectDefinitions);
        return ix > -1 ? objectDefinitions[ix].iconType : null;
    };

    const getObjectTypeFromFilename = async (filename) => {
        const objectDefinitions = await _loadObjectDefinitions();
        const extension = getFileExtensionFromFilename(filename);
        const ix = await getObjectDefinitionIndex('extension', extension, objectDefinitions);

        // Default to object-type Misc if unresolved.
        return ix > -1 ? objectDefinitions[ix].objectType : 'Misc';
    };

    const getObjectTypeFromMimeType = async (mimeType) => {
        const objectDefinitions = await _loadObjectDefinitions();
        const ix = await getObjectDefinitionIndex('mimeType', mimeType, objectDefinitions);

        // Default to object-type Misc if unresolved.
        return ix > -1 ? objectDefinitions[ix].objectType : 'Misc';
    };

    instance = {
        getLabelColor,
        getObjectDefinitionIndex,
        getFileExtensionFromFilename,
        getFileExtensionFromMimeType,
        getMimeTypeFromFileExtension,
        getMimeTypeFromFilename,
        getIconFromFilename,
        getIconFromMimeType,
        getObjectTypeFromFilename,
        getObjectTypeFromMimeType
    };

    return instance;
};